import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const TripHistory = ({ trips }) => {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('date-desc');
  const [filterBy, setFilterBy] = useState('all');

  const sortOptions = [
    { value: 'date-desc', label: 'Most Recent' },
    { value: 'date-asc', label: 'Oldest First' },
    { value: 'rating-desc', label: 'Highest Rated' },
    { value: 'rating-asc', label: 'Lowest Rated' }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Trips' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'reviewed', label: 'Reviewed' },
    { value: 'not-reviewed', label: 'Not Reviewed' }
  ];

  const filteredAndSortedTrips = trips?.filter(trip => {
      if (filterBy === 'all') return true;
      if (filterBy === 'completed') return trip?.status === 'completed';
      if (filterBy === 'cancelled') return trip?.status === 'cancelled';
      if (filterBy === 'reviewed') return trip?.hasReview;
      if (filterBy === 'not-reviewed') return !trip?.hasReview && trip?.status === 'completed';
      return true;
    })?.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.endDate) - new Date(a.endDate);
        case 'date-asc':
          return new Date(a.endDate) - new Date(b.endDate);
        case 'rating-desc':
          return (b?.rating || 0) - (a?.rating || 0);
        case 'rating-asc':
          return (a?.rating || 0) - (b?.rating || 0);
        default:
          return 0;
      }
    });

  const handleRebook = (trip) => {
    navigate(`/tour-detail?id=${trip?.tourId}&rebook=true`);
  };

  const handleWriteReview = (trip) => {
    navigate(`/tour-detail?id=${trip?.tourId}&review=true`);
  };

  const renderRating = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name="Star"
            size={14}
            className={star <= rating ? 'text-secondary fill-current' : 'text-muted-foreground'}
          />
        ))}
        <span className="text-sm text-muted-foreground ml-1">({rating})</span>
      </div>
    );
  };

  if (trips?.length === 0) {
    return (
      <div className="bg-card rounded-xl p-8 border border-border text-center">
        <Icon name="History" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Trip History</h3>
        <p className="text-muted-foreground mb-4">Your completed trips will appear here</p>
        <Button onClick={() => navigate('/homepage')} iconName="Compass" iconPosition="left">
          Explore Tours
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Select
          options={sortOptions}
          value={sortBy}
          onChange={setSortBy}
          placeholder="Sort by"
          className="sm:w-48"
        />
        <Select
          options={filterOptions}
          value={filterBy}
          onChange={setFilterBy}
          placeholder="Filter by"
          className="sm:w-48"
        />
      </div>
      {/* Trip List */}
      <div className="space-y-4">
        {filteredAndSortedTrips?.map((trip) => (
          <div key={trip?.id} className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-lg transition-shadow">
            <div className="flex flex-col lg:flex-row">
              {/* Trip Image */}
              <div className="lg:w-48 h-32 lg:h-auto relative overflow-hidden">
                <Image
                  src={trip?.image}
                  alt={trip?.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 left-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    trip?.status === 'completed' 
                      ? 'bg-success text-white' 
                      : trip?.status === 'cancelled' ?'bg-error text-white' :'bg-muted text-muted-foreground'
                  }`}>
                    {trip?.status?.charAt(0)?.toUpperCase() + trip?.status?.slice(1)}
                  </span>
                </div>
              </div>

              {/* Trip Details */}
              <div className="flex-1 p-4 lg:p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex-1 mb-4 lg:mb-0">
                    <h3 className="text-lg font-semibold text-foreground mb-2">{trip?.title}</h3>
                    <div className="space-y-1 mb-3">
                      <div className="flex items-center text-muted-foreground">
                        <Icon name="MapPin" size={14} className="mr-2" />
                        <span className="text-sm">{trip?.destination}</span>
                      </div>
                      <div className="flex items-center text-muted-foreground">
                        <Icon name="Calendar" size={14} className="mr-2" />
                        <span className="text-sm">
                          {new Date(trip.startDate)?.toLocaleDateString()} - {new Date(trip.endDate)?.toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center text-muted-foreground">
                        <Icon name="DollarSign" size={14} className="mr-2" />
                        <span className="text-sm">${trip?.totalPaid?.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Rating */}
                    {trip?.hasReview && trip?.rating && (
                      <div className="mb-3">
                        {renderRating(trip?.rating)}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col space-y-2 lg:ml-6">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRebook(trip)}
                      iconName="RotateCcw"
                      iconPosition="left"
                    >
                      Book Again
                    </Button>
                    {trip?.status === 'completed' && !trip?.hasReview && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleWriteReview(trip)}
                        iconName="Star"
                        iconPosition="left"
                      >
                        Write Review
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Download"
                      iconPosition="left"
                    >
                      Download Receipt
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredAndSortedTrips?.length === 0 && (
        <div className="bg-card rounded-xl p-8 border border-border text-center">
          <Icon name="Filter" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No trips match your filters</h3>
          <p className="text-muted-foreground">Try adjusting your filter criteria</p>
        </div>
      )}
    </div>
  );
};

export default TripHistory;